import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, ArrowLeft } from 'lucide-react';
import { api } from "@/services/auth-service";
import WhimsicalBackground from "@/components/WhimsicalBackground";

const ForgotPasswordPage = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isEmailSent, setIsEmailSent] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            await api.post('/auth/password/reset/', { email });
            setIsEmailSent(true);
        } catch (error) {
            setError(
                // @ts-expect-error ignoring
                error.response?.data?.error ||
                'Failed to send reset email. Please try again.'
            );
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="grid place-items-center">
            <WhimsicalBackground />

            <div className="w-full max-w-md p-6 space-y-6 shadow-lg bg-card/80 backdrop-blur-sm rounded-xl">
                <div className="space-y-2">
                    <Button
                        variant="ghost"
                        className="mb-4"
                        onClick={() => navigate('/signin')}
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Sign In
                    </Button>

                    <h1 className="text-2xl font-bold">Reset Password</h1>
                    <p className="text-muted-foreground">
                        {!isEmailSent
                            ? "Enter your email address and we'll send you instructions to reset your password."
                            : "Check your email for password reset instructions."
                        }
                    </p>
                </div>

                {!isEmailSent ? (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email Address</Label>
                            <div className="relative">
                                <Mail className="absolute w-4 h-4 left-3 top-3 text-muted-foreground" />
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="Enter your email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="pl-9"
                                    required
                                />
                            </div>
                            {error && (
                                <p className="text-sm text-destructive">{error}</p>
                            )}
                        </div>

                        <Button
                            type="submit"
                            className="w-full"
                            disabled={isLoading}
                        >
                            {isLoading ? "Sending..." : "Reset Password"}
                        </Button>
                    </form>
                ) : (
                    <div className="space-y-4">
                        <div className="p-4 rounded-lg bg-primary/10">
                            <p className="text-sm">
                                We've sent an email to <strong>{email}</strong> with instructions
                                to reset your password. The link will expire in 1 hour.
                            </p>
                        </div>
                        <Button
                            variant="outline"
                            className="w-full"
                            onClick={() => setIsEmailSent(false)}
                        >
                            Try different email
                        </Button>
                    </div>
                )}

                <div className="text-center">
                    <p className="text-sm text-muted-foreground">
                        Remember your password?{' '}
                        <Link
                            to="/signin"
                            className="text-primary hover:underline"
                        >
                            Sign in
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ForgotPasswordPage;
